import { chartMapper } from "components/Charts";
import InfoPanel from "components/Layout/InfoPanel";
import NotificationService from "components/Notification/Notification.service";
import Placeholder from "components/Layout/Placeholder";
import Widget from "components/Layout/Widget";
import { resources } from "config/resources";
import { schedulerStatsFieldName } from "config/resources/scheduler";
import { baseExecutionFilter } from "containers/ExecutionFilter/constants";
import {
    buildExecutionFilterPath,
    getExecutionFilterPath,
} from "containers/ExecutionFilter/filterStorage";
import { resourceDataFetchingSelector } from "containers/Resources/Resources.selectors";
import {
    useReconnectingWebsocket,
    useSecondaryLiveDataHold,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { wasSocketIntentionallyClosed } from "containers/Resources/screens/ResourcesSingle/socketIntentionalClose";
import { actions as statsActions } from "containers/Stats/Stats.reducer";
import { getStatData } from "containers/Stats/Stats.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFilterFieldsList, getFilterString, getIri, getSearchObj } from "utils/utils";
import "./ResourceStats.scss";

const ResourceStats = ({ resourceData, resourceName }) => {
    const dispatch = useDispatch();
    const resourceID = resourceData?.id;
    const workspaceData = useSelector(getWorkspaceData);
    // The execution live monitor resolves its filter-storage key from the current
    // workspace record, so key off the same id (falling back to the resource's workspace).
    const workspaceID =
        workspaceData?.id ??
        resourceData?.project_id_project?.workspace_id ??
        resourceData?.bundle_id_bundle?.workspace_id;
    const iri = getIri(resourceName, resourceID);
    const resourceDataFetching = useSelector(resourceDataFetchingSelector(resourceID));
    const isFetching = resourceDataFetching.includes(iri);
    const resourceConfig = resources.filter(
        (resource) => resource?.resourceName === resourceName
    )[0];

    const resourceStatsConfig = resourceConfig.statsSidebar;

    const resourceFilterField = getSearchObj({
        field: `${resourceName === "scheduler" ? schedulerStatsFieldName : resourceName}_id`,
        op: "eq",
        value: resourceData?.id,
    });

    const filters = [...baseExecutionFilter];

    const customFilter = getFilterFieldsList(resourceFilterField);

    // The execution live monitor reads its filter from localStorage (not the URL), so we
    // persist the filter on click and link to the clean sub-page URL. This keeps filters
    // out of the URL while still letting "Show all" open a pre-filtered view.
    const filterState = {
        filter: getFilterString(filters),
        customFilter: getFilterString(customFilter),
    };
    const link = workspaceID ? getExecutionFilterPath("workspace", workspaceID) : null;
    const onShowAllClick = () => {
        if (workspaceID) {
            buildExecutionFilterPath("workspace", workspaceID, filterState);
        }
    };

    const secondaryLiveDataHold = useSecondaryLiveDataHold();

    const { getSocket, deleteSocket } = useReconnectingWebsocket();

    useEffect(() => {
        if (!resourceStatsConfig || secondaryLiveDataHold) return;
        const cleanups = resourceStatsConfig.map((configItem) => {
            if (configItem?.websocket) {
                const reduxKey = configItem?.selector;

                configItem.graphql.variables.filter = `{"field":"${
                    configItem.resourceNameQueryField ?? resourceName
                }_id","op":"eq","value":"${resourceID}"}`;

                const websocketParams = Object.assign({}, configItem.graphql);
                dispatch({
                    type: statsActions.STORE_STAT_DATA,
                    iri: reduxKey,
                    response: null,
                });

                const websocketHost =
                    window.REACT_APP_WEBSOCKET_API || `wss://${window.location.host}`;
                const baseUrl = `${websocketHost}${
                    window.REACT_APP_WEBSOCKET_API_NAMESPACE
                }/graphql`;
                // Keep GraphQL out of the URL (CSRF + log leakage): connect to a
                // clean endpoint and send the subscription in the first websocket
                // message. Cache per widget (reduxKey) so distinct stats widgets
                // keep distinct sockets despite the shared connection URL.
                // `variables` stays an object with `filter` as a JSON string,
                // matching POST /graphql and the other first-message subscriptions.
                const socketUrl = `${baseUrl}?subscription_message=true`;
                const cacheKey = `${baseUrl}#${reduxKey}`;
                const socket = getSocket(socketUrl, cacheKey);

                socket.addEventListener("open", () => {
                    socket.send(JSON.stringify(websocketParams));
                });

                socket.addEventListener("message", (event) => {
                    dispatch({
                        type: statsActions.STORE_STAT_DATA,
                        iri: reduxKey,
                        response: JSON.parse(event.data),
                    });
                });
                // Surface connection failures instead of failing silently.
                // A stable toastId de-duplicates the reconnect storm and
                // collapses simultaneous failures of several stats widgets
                // into a single toast.
                socket.addEventListener("error", () => {
                    // Skip when we tore this socket down ourselves (unmount /
                    // navigation / tab-hidden) -- not a real live-data failure.
                    if (wasSocketIntentionallyClosed(socket)) {
                        return;
                    }
                    // Suppressed while the primary `/ui` socket is down (the top
                    // banner already signals the outage); fires only for an
                    // isolated failure while otherwise connected.
                    NotificationService.liveDataError(
                        "Could not load live data. Check your connection or refresh the page.",
                        { toastId: "resource-stats-live-data-error" }
                    );
                });
                return () => {
                    deleteSocket(cacheKey);
                };
            }

            if (configItem?.graphql || configItem?.rest) {
                new Promise((resolve, reject) => {
                    dispatch({
                        type: statsActions.START_FETCHING_STAT,
                        promise: { resolve, reject },
                        endpoint: configItem?.rest || "/graphql",
                        iri: configItem?.selector,
                        params: configItem?.restParams || {
                            query: configItem?.graphql?.query,
                            variables: {
                                ...configItem?.graphql?.variables,
                                filter: `{"field":"${resourceName}_id","op":"eq","value":"${resourceID}"}`,
                            },
                        },
                    });
                }).catch((error) => console.error(error));
            }
            return true;
        });
        return () => {
            cleanups.forEach((cleanup) => {
                cleanup();
            });
        };
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceID, secondaryLiveDataHold]);

    const statDataSelector = (state) => getStatData(state);
    const statData = useSelector(statDataSelector);

    return (
        <InfoPanel className="resourceStats">
            {isFetching ? (
                <Placeholder.List />
            ) : (
                <div className="flex flex-wrap">
                    {resourceStatsConfig &&
                        resourceStatsConfig.map((configEntry) => {
                            const WidgetComponent = chartMapper[configEntry.component];
                            return (
                                <Widget
                                    key={configEntry?.uid}
                                    className={configEntry?.className}
                                    title={configEntry?.title}
                                    subtitle={configEntry?.subtitle}
                                    link={configEntry?.hideShowAllLink ? null : link}
                                    onLinkClick={
                                        configEntry?.hideShowAllLink ? undefined : onShowAllClick
                                    }
                                >
                                    <WidgetComponent
                                        config={configEntry}
                                        stats={statData[configEntry.selector]}
                                        resourceData={resourceData}
                                    />
                                </Widget>
                            );
                        })}
                </div>
            )}
        </InfoPanel>
    );
};

ResourceStats.defaultProps = {};

ResourceStats.propTypes = {
    resourceData: PropTypes.oneOfType([PropTypes.object]).isRequired,
    resourceName: PropTypes.string.isRequired,
};

export default ResourceStats;
